import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Navigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Check,
  Loader2,
  Minus,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Pencil,
  AlertTriangle,
  X,
  Bot,
  Info,
  LayoutGrid,
  RefreshCw,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useLocation } from "../contexts/LocationContext";
import { usePermissions } from "@/hooks/usePermissions";

interface TableInfo {
  id: string;
  name: string;
  minCapacity: number;
  maxCapacity: number;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  shape: "rectangle" | "circle" | "square";
  room: { id: string; name: string };
}

interface TableCombination {
  id: string;
  name: string;
  minCapacity: number;
  maxCapacity: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  tables: TableInfo[];
  // true = combinación creada automáticamente al sentar una reserva en varias
  // mesas. Las automáticas HISTÓRICAS (anteriores a este campo) llegan con
  // `false` porque no se pudieron distinguir retroactivamente.
  isAdHoc?: boolean;
}

// Conjunto de mesas normalizado (orden-insensible) para comparar
// combinaciones por sus mesas exactas: duplicados en vivo (form) y
// duplicados ya existentes (lista).
function normalizeTableSet(tableIds: string[]): string {
  return [...tableIds].sort().join(",");
}

interface Room {
  id: string;
  name: string;
  gridSize?: number;
  tables: {
    id: string;
    name: string;
    minCapacity: number;
    maxCapacity: number;
    positionX: number;
    positionY: number;
    width: number;
    height: number;
    shape: "rectangle" | "circle" | "square";
    isActive: boolean;
  }[];
}

// Lienzo fijo: 50x50 celdas, igual que /tables y /reservations. `Room.gridSize`
// sigue llegando de la API pero ya no se usa para pintar — 50 es superconjunto
// de todos los tamaños de grid que existían antes, así que las posiciones
// guardadas siguen siendo válidas tal cual.
const CANVAS_CELLS = 50;
// Tamaño de celda DINÁMICO (zoom): vive en `useMapViewport`. Estas son sus
// cotas y su valor por defecto (sala vacía / antes de la primera medición).
const DEFAULT_CELL_PX = 18;
const MIN_CELL_PX = 8;
const MAX_CELL_PX = 48;
const ZOOM_STEP = 1.25;
// Margen alrededor de las mesas al auto-encuadrar, en celdas.
const FIT_MARGIN_CELLS = 1.5;
// Recuadro visible (viewport): tamaño FIJO en px (no vh/flex/zoom) para que
// el alto sea siempre determinista. El ancho se adapta hacia abajo si la
// pantalla es más estrecha que el recuadro.
const RECUADRO_PX = 540;

interface CellRect {
  positionX: number;
  positionY: number;
  width: number;
  height: number;
}

interface ContentBBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

// Bounding box (en celdas) de las mesas de una sala. `null` si está vacía →
// se usa el zoom por defecto en vez de intentar encuadrar la nada. (Esta
// página no tiene objetos decorativos, a diferencia de /tables y /reservations).
function computeContentBBox(tables: CellRect[]): ContentBBox | null {
  if (tables.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const t of tables) {
    minX = Math.min(minX, t.positionX);
    minY = Math.min(minY, t.positionY);
    maxX = Math.max(maxX, t.positionX + (t.width || 1));
    maxY = Math.max(maxY, t.positionY + (t.height || 1));
  }
  return { minX, minY, maxX, maxY };
}

function clampNum(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// Cuántas líneas de texto (de arriba a abajo, por orden de prioridad) caben
// en `boxHeightPx` sin cortarse a la mitad — o cabe la línea entera o no se
// pinta. `fontSizes` es el tamaño (px) de cada línea candidata.
function countFittingLines(boxHeightPx: number, fontSizes: number[]): number {
  const lineHeightFactor = 1.15;
  let used = 0;
  let count = 0;
  for (const fontPx of fontSizes) {
    const lineH = fontPx * lineHeightFactor;
    if (used + lineH > boxHeightPx + 0.5) break;
    used += lineH;
    count++;
  }
  return count;
}

// El NOMBRE de la mesa NUNCA se recorta con "…": se envuelve en como mucho 2
// líneas (`line-clamp-2` + `break-words` en el render) en vez de truncarse a
// una. Busca, bajando de 1 en 1 desde `idealFontPx` sin pasar de
// `minFontPx`, el mayor tamaño con el que el nombre completo quepa en 2
// líneas envueltas (estimación por caracteres, ancho medio ~0.5×fontPx). Si
// ni al tamaño mínimo cabe, se usa el mínimo igualmente: el `line-clamp-2`
// actúa como último recurso con "…" al final de la 2ª línea.
function fitNameToTwoLines(
  name: string,
  boxWidthPx: number,
  idealFontPx: number,
  minFontPx: number,
): { fontPx: number; lines: number } {
  const charsPerLine = (fontPx: number) =>
    Math.max(1, Math.floor(boxWidthPx / (fontPx * 0.5)));
  let fontPx = idealFontPx;
  while (fontPx > minFontPx && name.length > charsPerLine(fontPx) * 2) {
    fontPx -= 1;
  }
  const lines = name.length > charsPerLine(fontPx) ? 2 : 1;
  return { fontPx, lines };
}

/**
 * Viewport del lienzo: tamaño de celda dinámico (zoom) + auto-encuadre
 * (fit-to-content) + pan (botón central del ratón). El scroll nativo
 * (barras/trackpad) ya funciona solo con `overflow-auto` en el contenedor;
 * el pan añade además "arrastrar para desplazar" con el botón central — el
 * izquierdo ya está ocupado por el clic para seleccionar mesas. Mismo
 * patrón que TableMap.tsx/Reservations.tsx.
 */
// Estructura persistida en localStorage por superficie+sala (ver
// `storageKey`, formato `rm-map-view:<superficie>:<roomId>`).
interface SavedMapView {
  cellPx: number;
  scrollLeft: number;
  scrollTop: number;
}

function useMapViewport({
  totalCells,
  recuadroPx,
  defaultCellPx = DEFAULT_CELL_PX,
  minCellPx = MIN_CELL_PX,
  maxCellPx = MAX_CELL_PX,
  marginCells = FIT_MARGIN_CELLS,
  storageKey = null,
  panTouchEnabled = true,
}: {
  totalCells: number;
  recuadroPx: number;
  defaultCellPx?: number;
  minCellPx?: number;
  maxCellPx?: number;
  marginCells?: number;
  // Clave de localStorage (`rm-map-view:<superficie>:<roomId>`) para
  // persistir/restaurar cellPx+scroll. `null` = no persistir (p.ej. sin
  // sala seleccionada todavía).
  storageKey?: string | null;
  // Pan por arrastre táctil (además del scroll nativo, que SIEMPRE
  // funciona solo con `overflow-auto`, con o sin esto). Esta página es
  // siempre de solo lectura (clic para seleccionar mesas, sin drag), así
  // que se deja activado por defecto.
  panTouchEnabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cellPx, setCellPxRaw] = useState(defaultCellPx);
  // Ref "espejo" del cellPx actual: los closures de guardado (creados una
  // vez por `storageKey`, no por cada cambio de zoom) necesitan leer el
  // valor MÁS RECIENTE en el momento de guardar, no el que tenían cuando
  // se crearon. Se actualiza en cada render (patrón habitual de ref
  // "última versión conocida", no dispara re-render).
  const cellPxRef = useRef(cellPx);
  cellPxRef.current = cellPx;

  const clampCellPx = useCallback(
    (v: number) => clampNum(v, minCellPx, maxCellPx),
    [minCellPx, maxCellPx],
  );

  const zoomIn = useCallback(() => {
    setCellPxRaw((v) => clampCellPx(v * ZOOM_STEP));
  }, [clampCellPx]);

  const zoomOut = useCallback(() => {
    setCellPxRaw((v) => clampCellPx(v / ZOOM_STEP));
  }, [clampCellPx]);

  // Guardado con debounce (400ms) de la vista actual (cellPx + scroll) bajo
  // `storageKey`. Se dispara en cada cambio de `cellPx` (zoom/fit/restore)
  // y en cada evento `scroll` nativo del contenedor (pan con ratón/touch,
  // scroll con rueda/barras). `localStorage` puede fallar (modo privado,
  // cuota) — nunca debe romper el mapa, solo no persiste.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSave = useCallback(() => {
    if (!storageKey) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      try {
        const data: SavedMapView = {
          cellPx: cellPxRef.current,
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop,
        };
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch {
        // localStorage no disponible (privado/cuota) — no persiste, no rompe el mapa
      }
    }, 400);
  }, [storageKey]);

  useEffect(() => {
    scheduleSave();
  }, [cellPx, scheduleSave]);

  const onScroll = useCallback(() => {
    scheduleSave();
  }, [scheduleSave]);

  // Auto-encuadre: calcula `cellPx` a partir del bounding box del contenido
  // (en celdas) y desplaza el scroll para dejarlo visible. Usa el
  // ancho/alto REALES del contenedor (`clientWidth`/`clientHeight`), NO
  // `recuadroPx` a pelo — en viewports estrechos (móvil) el recuadro real
  // es más estrecho que `recuadroPx` (que ya solo actúa como tope máximo
  // vía `maxWidth`), así que dividir por `recuadroPx` ahí sobrestimaba el
  // espacio disponible y dejaba el lienzo con muy poca área realmente
  // visible ("se ve pequeño"). Con fallback a `recuadroPx` solo si el
  // contenedor aún no ha montado (medida 0). El `rAF` es necesario porque
  // el nuevo tamaño del lienzo (ligado a `cellPx`) tarda un ciclo de
  // render + layout en aplicarse antes de que scrollLeft/scrollTop tengan
  // efecto real.
  const fitToContent = useCallback(
    (bbox: ContentBBox | null) => {
      const viewportWidthPx = containerRef.current?.clientWidth || recuadroPx;
      const viewportHeightPx =
        containerRef.current?.clientHeight || recuadroPx;
      if (!bbox) {
        setCellPxRaw(defaultCellPx);
        requestAnimationFrame(() => {
          const el = containerRef.current;
          if (el) {
            el.scrollLeft = 0;
            el.scrollTop = 0;
          }
        });
        return;
      }
      const minX = Math.max(0, bbox.minX - marginCells);
      const minY = Math.max(0, bbox.minY - marginCells);
      const maxX = Math.min(totalCells, bbox.maxX + marginCells);
      const maxY = Math.min(totalCells, bbox.maxY + marginCells);
      const widthCells = Math.max(1, maxX - minX);
      const heightCells = Math.max(1, maxY - minY);
      const next = clampCellPx(
        Math.min(viewportWidthPx / widthCells, viewportHeightPx / heightCells),
      );
      setCellPxRaw(next);
      requestAnimationFrame(() => {
        const el = containerRef.current;
        if (!el) return;
        el.scrollLeft = minX * next;
        el.scrollTop = minY * next;
      });
    },
    [clampCellPx, defaultCellPx, marginCells, recuadroPx, totalCells],
  );

  // Al cambiar de sala: restaura la vista guardada (cellPx + scroll) si
  // existe una para `storageKey`; si no hay nada guardado (o falla el
  // parseo/localStorage), cae al auto-encuadre de siempre.
  const restoreOrFit = useCallback(
    (bbox: ContentBBox | null) => {
      if (storageKey) {
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) {
            const saved = JSON.parse(raw) as Partial<SavedMapView>;
            if (
              typeof saved.cellPx === "number" &&
              typeof saved.scrollLeft === "number" &&
              typeof saved.scrollTop === "number"
            ) {
              const next = clampCellPx(saved.cellPx);
              setCellPxRaw(next);
              requestAnimationFrame(() => {
                const el = containerRef.current;
                if (!el) return;
                el.scrollLeft = saved.scrollLeft as number;
                el.scrollTop = saved.scrollTop as number;
              });
              return;
            }
          }
        } catch {
          // JSON corrupto / localStorage no disponible — se ignora y se hace fit
        }
      }
      fitToContent(bbox);
    },
    [storageKey, clampCellPx, fitToContent],
  );

  // Pan con botón central del ratón (desktop) — scroll nativo (barras/
  // trackpad/rueda) siempre funciona vía `overflow-auto`; esto añade
  // además "arrastrar para desplazar" sin interferir con el clic para
  // seleccionar mesas (botón izquierdo).
  const panStateRef = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 1) return; // solo botón central
    const container = containerRef.current;
    if (!container) return;
    e.preventDefault();
    panStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
    setIsPanning(true);
  }, []);

  useEffect(() => {
    if (!isPanning) return;
    const handleMove = (e: MouseEvent) => {
      const state = panStateRef.current;
      const container = containerRef.current;
      if (!state || !container) return;
      container.scrollLeft = state.scrollLeft - (e.clientX - state.startX);
      container.scrollTop = state.scrollTop - (e.clientY - state.startY);
    };
    const handleUp = () => {
      panStateRef.current = null;
      setIsPanning(false);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isPanning]);

  // Pan táctil (opcional, `panTouchEnabled`): el scroll nativo por
  // arrastre de un dedo YA funciona en cualquier `overflow-auto` sin nada
  // de esto — esto es solo para tener control total del gesto (evita que
  // el scroll nativo y este pan compitan). Umbral de 6px antes de
  // considerar que es un arrastre (no un tap) para no robarle el toque a
  // los clics de seleccionar mesa.
  const touchStateRef = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
    engaged: boolean;
  } | null>(null);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!panTouchEnabled) return;
      const container = containerRef.current;
      if (!container || e.touches.length !== 1) return;
      const touch = e.touches[0];
      touchStateRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
        engaged: false,
      };
    },
    [panTouchEnabled],
  );

  useEffect(() => {
    if (!panTouchEnabled) return;
    const handleTouchMove = (e: TouchEvent) => {
      const state = touchStateRef.current;
      const container = containerRef.current;
      if (!state || !container || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;
      if (!state.engaged) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return; // aún podría ser un tap
        state.engaged = true;
      }
      e.preventDefault(); // ya lo llevamos nosotros: evita que el scroll nativo compita
      container.scrollLeft = state.scrollLeft - dx;
      container.scrollTop = state.scrollTop - dy;
    };
    const handleTouchEnd = () => {
      touchStateRef.current = null;
    };
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);
    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [panTouchEnabled]);

  return {
    containerRef,
    cellPx,
    zoomIn,
    zoomOut,
    fitToContent,
    restoreOrFit,
    onScroll,
    isPanning,
    onMouseDown,
    onTouchStart,
  };
}

// Botonera flotante de zoom (+/-/ajustar), mismo patrón que en
// TableMap.tsx/Reservations.tsx.
function ZoomControls({
  onZoomIn,
  onZoomOut,
  onFit,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
}) {
  // w-11 h-11 = 44px: área táctil mínima recomendada (Apple HIG), holgada
  // también en desktop con el ratón.
  const btnClass =
    "w-11 h-11 flex items-center justify-center bg-white border border-gray-300 rounded-lg shadow-sm text-gray-600 hover:bg-gray-50 active:bg-gray-100 hover:text-gray-900";
  return (
    <div className="absolute top-2 right-2 z-[70] flex flex-col gap-1.5">
      <button
        type="button"
        onClick={onZoomIn}
        className={btnClass}
        title="Acercar"
      >
        <ZoomIn className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={onZoomOut}
        className={btnClass}
        title="Alejar"
      >
        <ZoomOut className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={onFit}
        className={btnClass}
        title="Ajustar al contenido"
      >
        <Maximize2 className="w-5 h-5" />
      </button>
    </div>
  );
}

// Extrae un mensaje de error legible del cuerpo JSON (`error.response.data`
// de axios) de una respuesta fallida de la API. Este módulo
// (`table-combinations`) devuelve, según el caso: `{ message }` en el 403 de
// `requirePermission` (F2), `{ error, details: ZodIssue[] }` en los 400 de
// validación (el `details[0].message` es el issue específico, más útil que
// el `error` genérico), o simplemente `{ error }` en 404/409/500. Se
// prioriza en ese orden; si no hay nada reconocible, se usa `fallback`.
function extractApiErrorMessage(errData: unknown, fallback: string): string {
  if (errData && typeof errData === "object") {
    const obj = errData as {
      message?: string;
      details?: { message?: string }[];
      error?: string;
    };
    if (typeof obj.message === "string" && obj.message) return obj.message;
    const firstDetail = Array.isArray(obj.details)
      ? obj.details.find((d) => typeof d?.message === "string" && d.message)
      : undefined;
    if (firstDetail?.message) return firstDetail.message;
    if (typeof obj.error === "string" && obj.error) return obj.error;
  }
  return fallback;
}

export default function TableCombinations() {
  const { locationVersion, currentBookingMode } = useLocation();
  const { can } = usePermissions();

  // Gating por permiso `mapa:operar` (owner/admin siempre pasan vía
  // `usePermissions`; staff según su configuración). El backend restringe
  // igual las mutaciones de este módulo (`requirePermission("mapa:operar")`
  // en las 4 rutas de escritura) — esto solo evita que un staff sin permiso
  // llegue a ver los controles habilitados y descubra el 403 a botonazos.
  const canEdit = can("mapa:operar");

  // State
  const [combinations, setCombinations] = useState<TableCombination[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  // Error de CARGA (fetchData), distinto de "no hay salas"/"no hay
  // combinaciones" (arrays vacíos reales tras una carga exitosa). El render
  // usa esto para mostrar un estado persistente con "Reintentar" en vez de
  // depender del `message` de abajo, que se autooculta a los 5s.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  // Avisos devueltos por el backend al borrar una combinación que tenía
  // reservas asignadas (`warnings` opcional en la respuesta del DELETE).
  // Aparte del `message` de éxito porque no debe autoocultarse a los 5s.
  const [deleteWarnings, setDeleteWarnings] = useState<string[] | null>(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Form state para nueva combinación / edición de una existente. Este panel
  // (el de "Añadir combinación") hace de formulario único para crear Y
  // editar — al pulsar "Editar" en una combinación existente se precarga
  // aquí su selección de mesas y capacidades (el backend acepta `tableIds`
  // también en el PUT, con el mismo 409 de duplicado que el POST).
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);
  const [maxCapacity, setMaxCapacity] = useState(0);
  const [minCapacity, setMinCapacity] = useState(0);
  // null = modo "crear"; id = editando esa combinación existente.
  const [editingComboId, setEditingComboId] = useState<string | null>(null);
  // Si el usuario ha tocado a mano el campo Máximo en esta sesión del
  // formulario (se resetea cada vez que se abre: nueva combinación o
  // "Editar"). Mientras esté en `false`, el máximo se recalcula solo con la
  // suma de capacidades de las mesas seleccionadas.
  const [maxCapacityDirty, setMaxCapacityDirty] = useState(false);
  // Error mostrado DENTRO del formulario (validación cliente + red de
  // seguridad del 409 del backend). Independiente del `message` de arriba.
  const [formError, setFormError] = useState<string | null>(null);
  // Ocultar combinaciones automáticas (isAdHoc) de la lista. Por defecto se
  // muestran todas.
  const [hideAutomatic, setHideAutomatic] = useState(false);

  // Viewport del lienzo (zoom + auto-encuadre + pan). El lienzo es fijo
  // (CANVAS_CELLS); `Room.gridSize` ya no se usa para pintar. Esta página
  // es siempre de solo lectura (clic para seleccionar mesas, sin drag), así
  // que el pan táctil de fondo se deja activado.
  const viewport = useMapViewport({
    totalCells: CANVAS_CELLS,
    recuadroPx: RECUADRO_PX,
    storageKey: selectedRoom
      ? `rm-map-view:combinations:${selectedRoom.id}`
      : null,
    panTouchEnabled: true,
  });

  // Cargar datos
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [combinationsRes, roomsRes] = await Promise.all([
        apiClient.get("/table-combinations"),
        apiClient.get("/tables/rooms"),
      ]);
      setCombinations(combinationsRes.data);
      const loadedRooms = roomsRes.data.rooms || [];
      setRooms(loadedRooms);
      if (loadedRooms.length > 0 && !selectedRoom) {
        setSelectedRoom(loadedRooms[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      const axiosError = error as { response?: { data?: unknown } };
      const errorMessage = extractApiErrorMessage(
        axiosError.response?.data,
        "Error al cargar los datos",
      );
      setLoadError(errorMessage);
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [selectedRoom]);

  // Reload data when location changes - reset selected room first
  useEffect(() => {
    setSelectedRoom(null); // Reset so first room of new location is selected
    setCombinations([]); // Clear old combinations
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationVersion]);

  // Al cargar/cambiar de sala: restaura la vista guardada (cellPx+scroll)
  // si existe, si no auto-encuadra. Deliberadamente keyed solo por `.id`
  // (no por `selectedRoom` completo ni por `rooms`) para NO
  // re-encuadrar/resetear la vista en cada refresco de datos (fetchData()
  // se llama tras casi cualquier mutación). El botón "Ajustar" de
  // ZoomControls permite re-encuadrar a mano en cualquier momento (y
  // actualiza lo guardado).
  useEffect(() => {
    if (!selectedRoom) return;
    const bbox = computeContentBBox(selectedRoom.tables ?? []);
    viewport.restoreOrFit(bbox);
  }, [selectedRoom?.id, viewport.restoreOrFit]);

  // Calcular capacidad sugerida
  const calculateSuggestedCapacity = useCallback(
    (tableIds: string[]): { min: number; max: number } => {
      let totalMin = 0;
      let totalMax = 0;
      for (const room of rooms) {
        for (const table of room.tables) {
          if (tableIds.includes(table.id)) {
            totalMin += table.minCapacity;
            totalMax += table.maxCapacity;
          }
        }
      }
      return { min: totalMin, max: totalMax };
    },
    [rooms]
  );

  // Id+nombre(+sala) de las mesas seleccionadas, para pintar los chips con
  // un botón de deseleccionar cada una. Necesario (no basta con el nombre)
  // porque una mesa desactivada tras haber formado parte de una
  // combinación no aparece en el mapa (queda oculta) y sin este id no
  // habría forma de quitarla de la selección — quedaría "atascada" al
  // editar esa combinación (F6).
  // `roomName` desambigua mesas homónimas de salas distintas (p.ej. "Mesa
  // 22" puede existir a la vez en "Salón" y en "Feria Casa Ceuta"): el chip
  // por sí solo no bastaba para saber de qué sala era cada una.
  const getSelectedTableEntries = (): {
    id: string;
    name: string;
    roomName: string;
  }[] => {
    const entries: { id: string; name: string; roomName: string }[] = [];
    for (const room of rooms) {
      for (const table of room.tables) {
        if (selectedTableIds.includes(table.id)) {
          entries.push({ id: table.id, name: table.name, roomName: room.name });
        }
      }
    }
    return entries.sort((a, b) => a.name.localeCompare(b.name));
  };

  // Nombre(s) de sala de las mesas de una combinación YA GUARDADA (lista de
  // "Combinaciones existentes"). El backend ya manda `room` por mesa en
  // cada combinación (`combo.tables[].room`), pero antes no se usaba en
  // ningún sitio: dos combinaciones con mesas homónimas de salas distintas
  // eran indistinguibles en la lista (solo se veía `combo.name`, un texto
  // generado a partir de los NOMBRES de mesa, sin sala).
  const getComboRoomNames = (combo: TableCombination): string[] => {
    const names = new Set<string>();
    for (const t of combo.tables) {
      if (t.room?.name) names.add(t.room.name);
    }
    return Array.from(names).sort();
  };

  // ¿Pertenece esta combinación a la sala `roomId` (la pestaña activa del
  // mapa)? Se basa en `combo.tables[].room.id`, que es la única sala que el
  // backend ya manda por mesa. Criterio "alguna mesa en esa sala" en vez de
  // "todas": para una combinación normal (una sola sala) da el mismo
  // resultado exacto que "todas", pero si una combinación llegara a mezclar
  // mesas de varias salas, con "alguna" se muestra en TODAS las salas
  // implicadas en vez de ocultarse por completo en cualquiera de ellas. Si
  // ninguna mesa trae la sala resuelta (dato incompleto), no se oculta por
  // duda: se muestra siempre.
  const comboMatchesRoom = (combo: TableCombination, roomId: string): boolean => {
    const resolvedRoomIds = combo.tables
      .map((t) => t.room?.id)
      .filter((id): id is string => Boolean(id));
    if (resolvedRoomIds.length === 0) return true;
    return resolvedRoomIds.includes(roomId);
  };

  // Salas a las que pertenecen las mesas seleccionadas (aviso "salas
  // mezcladas" no bloqueante).
  const getSelectedRoomNames = useCallback((): string[] => {
    const names = new Set<string>();
    for (const room of rooms) {
      for (const table of room.tables) {
        if (selectedTableIds.includes(table.id)) names.add(room.name);
      }
    }
    return Array.from(names);
  }, [rooms, selectedTableIds]);

  const selectedRoomNames = useMemo(
    () => getSelectedRoomNames(),
    [getSelectedRoomNames]
  );
  const hasMixedRooms = selectedRoomNames.length > 1;

  // Combinación duplicada EN VIVO: el conjunto de mesas seleccionado (orden-
  // insensible) coincide con una combinación ya cargada, excluyendo la que
  // se está editando. Con menos de 2 mesas no puede haber coincidencia real
  // (toda combinación tiene mínimo 2 mesas).
  const liveDuplicateMatch = useMemo(() => {
    if (selectedTableIds.length < 2) return null;
    const normalizedSelection = normalizeTableSet(selectedTableIds);
    return (
      combinations.find(
        (c) =>
          c.id !== editingComboId &&
          normalizeTableSet(c.tables.map((t) => t.id)) === normalizedSelection
      ) || null
    );
  }, [combinations, selectedTableIds, editingComboId]);

  // Capacidad física sugerida (suma de máximos/mínimos) de la selección
  // actual — se usa para el prellenado del máximo y para el aviso de
  // "supera la capacidad física".
  const suggestedCapacity = useMemo(
    () => calculateSuggestedCapacity(selectedTableIds),
    [calculateSuggestedCapacity, selectedTableIds]
  );
  const exceedsPhysicalCapacity =
    selectedTableIds.length > 0 && maxCapacity > suggestedCapacity.max;
  const capacityRangeError =
    minCapacity > maxCapacity
      ? "El mínimo no puede ser mayor que el máximo"
      : null;

  // Combinaciones ya existentes que comparten EXACTAMENTE el mismo conjunto
  // de mesas que otra: se agrupan por conjunto normalizado; en cada grupo la
  // más antigua (por createdAt) se considera "la original" y el resto se
  // marcan como duplicadas de ella.
  const { duplicateInfoByComboId, totalDuplicateSurplus } = useMemo(() => {
    const groups = new Map<string, TableCombination[]>();
    for (const combo of combinations) {
      const key = normalizeTableSet(combo.tables.map((t) => t.id));
      const group = groups.get(key);
      if (group) group.push(combo);
      else groups.set(key, [combo]);
    }
    const info = new Map<string, { originalName: string }>();
    let surplus = 0;
    for (const group of groups.values()) {
      if (group.length < 2) continue;
      const sorted = [...group].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      const [original, ...duplicates] = sorted;
      for (const dup of duplicates) {
        info.set(dup.id, { originalName: original.name });
      }
      surplus += duplicates.length;
    }
    return { duplicateInfoByComboId: info, totalDuplicateSurplus: surplus };
  }, [combinations]);

  // Filtrado en 2 pasos, en este orden: 1) "Ocultar automáticas" (ya
  // existía), 2) sala activa del mapa (nuevo). El resultado intermedio
  // (`afterAutomaticFilter`) se guarda aparte solo para poder distinguir en
  // el mensaje de "lista vacía" cuál de los dos filtros es el responsable.
  // El filtro de sala solo se aplica con más de una sala (`rooms.length >
  // 1`): con una sola sala no tiene sentido filtrar y se muestran todas.
  const afterAutomaticFilter = hideAutomatic
    ? combinations.filter((c) => !c.isAdHoc)
    : combinations;
  const visibleCombinations =
    rooms.length > 1 && selectedRoom
      ? afterAutomaticFilter.filter((c) =>
          comboMatchesRoom(c, selectedRoom.id),
        )
      : afterAutomaticFilter;

  // Toggle selección de mesa (F2: gateado por `canEdit` — sin permiso de
  // `mapa:operar`, el mapa es solo consulta).
  const handleTableClick = (tableId: string) => {
    if (!canEdit) return;
    setFormError(null);
    setSelectedTableIds((prev) => {
      const newIds = prev.includes(tableId)
        ? prev.filter((id) => id !== tableId)
        : [...prev, tableId];

      // Auto-calcular capacidades: el mínimo siempre se recalcula; el
      // máximo solo si el usuario no lo ha tocado a mano en esta sesión del
      // formulario (`maxCapacityDirty`).
      const { min, max } = calculateSuggestedCapacity(newIds);
      setMinCapacity(min);
      setMaxCapacity((prevMax) => (maxCapacityDirty ? prevMax : max));

      return newIds;
    });
  };

  // F7: clamp a mínimo 1 (como el editor rápido) — Añadir/Guardar no debe
  // poder enviarse con una capacidad de 0.
  const handleMaxCapacityChange = (value: number) => {
    setMaxCapacity(Math.max(1, value));
    setMaxCapacityDirty(true);
    setFormError(null);
  };

  const handleMinCapacityChange = (value: number) => {
    setMinCapacity(Math.max(1, value));
    setFormError(null);
  };

  const findRoomForTable = (tableId: string): Room | null => {
    for (const room of rooms) {
      if (room.tables.some((t) => t.id === tableId)) return room;
    }
    return null;
  };

  // Cargar una combinación existente en el formulario para editarla
  // (incluida su selección de mesas — el backend acepta `tableIds` en el
  // PUT). Se marca el máximo como "dirty" porque el valor de partida ya es
  // una decisión guardada, no una sugerencia: no debe recalcularse solo
  // porque el usuario añada/quite una mesa.
  const startEdit = (combo: TableCombination) => {
    setEditingComboId(combo.id);
    setSelectedTableIds(combo.tables.map((t) => t.id));
    setMaxCapacity(combo.maxCapacity);
    setMinCapacity(combo.minCapacity);
    setMaxCapacityDirty(true);
    setFormError(null);
    const firstTableRoom =
      combo.tables.length > 0 ? findRoomForTable(combo.tables[0].id) : null;
    if (firstTableRoom && firstTableRoom.id !== selectedRoom?.id) {
      setSelectedRoom(firstTableRoom);
    }
  };

  const cancelEdit = () => {
    setEditingComboId(null);
    setSelectedTableIds([]);
    setMaxCapacity(0);
    setMinCapacity(0);
    setMaxCapacityDirty(false);
    setFormError(null);
  };

  // Crear o guardar cambios de una combinación (formulario unificado).
  const handleSaveCombination = async () => {
    if (!canEdit) return;
    if (selectedTableIds.length < 2) {
      setFormError("Debes seleccionar al menos 2 mesas");
      return;
    }
    if (capacityRangeError) {
      setFormError(capacityRangeError);
      return;
    }
    if (liveDuplicateMatch) {
      setFormError(
        `Ya existe una combinación con estas mesas: «${liveDuplicateMatch.name}»`
      );
      return;
    }

    try {
      setSaving(true);
      setFormError(null);
      if (editingComboId) {
        await apiClient.put(`/table-combinations/${editingComboId}`, {
          maxCapacity,
          minCapacity,
          tableIds: selectedTableIds,
        });
        setMessage({
          type: "success",
          text: "Combinación actualizada correctamente",
        });
      } else {
        await apiClient.post("/table-combinations", {
          maxCapacity,
          minCapacity,
          tableIds: selectedTableIds,
        });
        setMessage({
          type: "success",
          text: "Combinación creada correctamente",
        });
      }
      cancelEdit();
      fetchData();
    } catch (error: unknown) {
      console.error("Error saving combination:", error);
      const axiosError = error as { response?: { data?: unknown } };
      // Red de seguridad: si aun así llega un 409 (u otro error, incluido
      // el 403 de `requirePermission` si el permiso se revocó a mitad de
      // sesión) del backend, se muestra dentro del formulario, nunca en
      // silencio.
      setFormError(
        extractApiErrorMessage(
          axiosError.response?.data,
          "Error al guardar la combinación",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  // F8 — Editor rápido de capacidad de cada tarjeta: antes cada tecla/clic
  // llamaba a esto directamente (PUT + `fetchData()`, es decir 2 GET:
  // combinations+rooms). Ahora los valores "en edición" se acumulan en
  // `draftCombo` (respuesta visual instantánea) y solo se confirman contra
  // el backend con debounce (500ms) — `scheduleCapacityChange` — o al
  // instante al perder el foco del input — `flushCapacityChange`. Al
  // confirmar, solo se refresca `/table-combinations` (esta edición no
  // toca mesas/salas, no hace falta releer `/tables/rooms`).
  const [draftCombo, setDraftCombo] = useState<
    Record<string, { maxCapacity: number; minCapacity: number }>
  >({});
  // Ref "espejo" de `draftCombo`: el temporizador de debounce se dispara
  // fuera del ciclo de render y necesita leer el valor MÁS RECIENTE, no el
  // que había en el momento en que se programó (mismo patrón que
  // `cellPxRef` en `useMapViewport`).
  const draftComboRef = useRef(draftCombo);
  draftComboRef.current = draftCombo;
  const capacityTimersRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});

  useEffect(() => {
    // Limpieza de temporizadores pendientes al desmontar la página.
    const timers = capacityTimersRef.current;
    return () => {
      Object.values(timers).forEach((t) => clearTimeout(t));
    };
  }, []);

  const getDraftCapacity = (
    combo: TableCombination,
  ): { maxCapacity: number; minCapacity: number } =>
    draftCombo[combo.id] ?? {
      maxCapacity: combo.maxCapacity,
      minCapacity: combo.minCapacity,
    };

  // Confirma contra el backend los valores actuales de `draftComboRef` (o
  // los recién pasados en `overrides`, útil justo tras programarlos porque
  // el estado aún no se ha re-renderizado). Se valida mín ≤ máx antes de
  // enviar.
  const commitCapacity = useCallback(
    async (
      id: string,
      overrides?: { maxCapacity?: number; minCapacity?: number },
    ) => {
      const combo = combinations.find((c) => c.id === id);
      const draft = draftComboRef.current[id];
      const nextMax =
        overrides?.maxCapacity ??
        draft?.maxCapacity ??
        combo?.maxCapacity ??
        1;
      const nextMin =
        overrides?.minCapacity ??
        draft?.minCapacity ??
        combo?.minCapacity ??
        1;
      if (nextMin > nextMax) {
        setMessage({
          type: "error",
          text: "El mínimo no puede ser mayor que el máximo",
        });
        return;
      }
      try {
        setSaving(true);
        await apiClient.put(`/table-combinations/${id}`, {
          maxCapacity: nextMax,
          minCapacity: nextMin,
        });
        const res = await apiClient.get("/table-combinations");
        setCombinations(res.data);
      } catch (error) {
        console.error("Error updating combination:", error);
        const axiosError = error as { response?: { data?: unknown } };
        setMessage({
          type: "error",
          text: extractApiErrorMessage(
            axiosError.response?.data,
            "Error al actualizar",
          ),
        });
      } finally {
        setSaving(false);
        setDraftCombo((prev) => {
          if (!(id in prev)) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [combinations],
  );

  // Actualiza el valor "en edición" al instante y reinicia el temporizador
  // de 500ms: clics/teclas seguidas lo reprograman, así que solo se envía
  // UN `PUT` cuando el usuario deja de tocar el control.
  const scheduleCapacityChange = useCallback(
    (
      combo: TableCombination,
      field: "maxCapacity" | "minCapacity",
      value: number,
    ) => {
      const current =
        draftComboRef.current[combo.id] ??
        ({
          maxCapacity: combo.maxCapacity,
          minCapacity: combo.minCapacity,
        } as { maxCapacity: number; minCapacity: number });
      const next = { ...current, [field]: Math.max(1, value) };
      setDraftCombo((prev) => ({ ...prev, [combo.id]: next }));
      if (capacityTimersRef.current[combo.id]) {
        clearTimeout(capacityTimersRef.current[combo.id]);
      }
      capacityTimersRef.current[combo.id] = setTimeout(() => {
        commitCapacity(combo.id);
      }, 500);
    },
    [commitCapacity],
  );

  // Confirma de inmediato (sin esperar el debounce) — usado en `onBlur` de
  // los inputs para no dejar un cambio pendiente si el usuario navega a
  // otra parte de la página.
  const flushCapacityChange = useCallback(
    (comboId: string) => {
      if (capacityTimersRef.current[comboId]) {
        clearTimeout(capacityTimersRef.current[comboId]);
        delete capacityTimersRef.current[comboId];
      }
      if (draftComboRef.current[comboId]) {
        commitCapacity(comboId);
      }
    },
    [commitCapacity],
  );

  // Eliminar combinación
  const handleDelete = async (id: string) => {
    if (!canEdit) return;
    setConfirmDialog({
      show: true,
      title: "Eliminar combinación",
      message: "¿Estás seguro de eliminar esta combinación?",
      onConfirm: async () => {
        setConfirmDialog({
          show: false,
          title: "",
          message: "",
          onConfirm: () => {},
        });
        try {
          setSaving(true);
          setDeleteWarnings(null);
          const res = await apiClient.delete(`/table-combinations/${id}`);
          setMessage({
            type: "success",
            text: "Combinación eliminada correctamente",
          });
          // F10: el backend puede devolver `warnings` (no bloqueante) si la
          // combinación borrada tenía reservas asignadas. Se muestra en un
          // aviso aparte del toast de éxito, ya que ese se autooculta a
          // los 5s y esto conviene que el usuario lo vea con calma.
          const data = res.data as { warnings?: unknown } | undefined;
          const warnings = Array.isArray(data?.warnings)
            ? (data?.warnings as string[])
            : [];
          if (warnings.length > 0) setDeleteWarnings(warnings);
          if (editingComboId === id) cancelEdit();
          fetchData();
        } catch (error) {
          console.error("Error deleting combination:", error);
          const axiosError = error as { response?: { data?: unknown } };
          setMessage({
            type: "error",
            text: extractApiErrorMessage(
              axiosError.response?.data,
              "Error al eliminar la combinación",
            ),
          });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // Cambiar sala. Mientras se edita una combinación se conserva la
  // selección/capacidades al cambiar de pestaña (para poder revisar o
  // completar combinaciones que abarcan varias salas); si no se está
  // editando, cambiar de sala reinicia el formulario de creación como antes.
  const handleRoomChange = (room: Room) => {
    setSelectedRoom(room);
    if (editingComboId) return;
    setSelectedTableIds([]);
    setMaxCapacity(0);
    setMinCapacity(0);
  };

  // Auto-hide messages
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Check if table is part of a combination. Usa `visibleCombinations` (no
  // `combinations`) para que el resaltado naranja "con combinación" en el
  // mapa sea coherente con el filtro "Ocultar automáticas": si esa
  // combinación está oculta, la mesa no debe pintarse como si la tuviera
  // (F5) — mismo criterio que el tooltip, que usa el resultado de esta
  // función.
  const getTableCombinations = (tableId: string): TableCombination[] => {
    return visibleCombinations.filter((c) =>
      c.tables.some((t) => t.id === tableId),
    );
  };

  // F1: en modo citas las combinaciones de mesas no aplican (los "salones"
  // de citas son otro modelo). El sidebar ya oculta el enlace; esto cubre
  // el acceso por URL directa. AFTER all hooks (no rompe las reglas de
  // hooks) — mismo criterio que TableMap.tsx.
  if (currentBookingMode === "appointments") {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Cargando...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          Combinaciones de Mesas
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Selecciona mesas en el mapa para crear combinaciones para grupos
          grandes
        </p>
      </div>

      {/* F2: nota de solo-lectura para staff sin el permiso `mapa:operar` —
          el backend restringe estas mutaciones con `requirePermission`; se
          avisa aquí en vez de dejar que el usuario descubra el 403 botón a
          botón. */}
      {!canEdit && (
        <div className="p-2.5 md:p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs md:text-sm text-gray-500 flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Solo con permiso de gestión del mapa puedes editar combinaciones.
            Puedes consultar el mapa y la lista, pero no crear, modificar ni
            eliminar combinaciones.
          </span>
        </div>
      )}

      {/* Messages */}
      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* F10: aviso del backend tras un borrado, si la combinación tenía
          reservas asignadas (`warnings` opcional en la respuesta del
          DELETE). Aparte del `message` de éxito porque este no se
          autooculta a los 5s — el usuario lo cierra a mano. */}
      {deleteWarnings && deleteWarnings.length > 0 && (
        <div className="p-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200 flex items-start gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div className="flex-1 space-y-0.5">
            {deleteWarnings.map((warning, index) => (
              <p key={index}>{warning}</p>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setDeleteWarnings(null)}
            className="shrink-0 text-amber-600 hover:text-amber-900"
            title="Cerrar aviso"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Aviso de combinaciones duplicadas ya existentes (mismo conjunto de
          mesas). El borrado se hace con el botón de eliminar que ya existe
          en cada tarjeta duplicada — este banner solo informa. */}
      {totalDuplicateSurplus > 0 && (
        <div className="p-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200 flex items-center gap-2">
          <AlertTriangle size={16} className="shrink-0" />
          <span>
            Tienes {totalDuplicateSurplus}{" "}
            {totalDuplicateSurplus === 1
              ? "combinación duplicada"
              : "combinaciones duplicadas"}
          </span>
        </div>
      )}

      {/* F3: error de CARGA distinto de "no hay salas"/"no hay
          combinaciones" (arrays vacíos reales tras una carga exitosa) — sin
          esto, un fallo de red/permiso dejaba `rooms`/`combinations` vacíos
          y se pintaban igualmente los carteles de vacío feliz, que
          engañaban (parecía que no había datos, no que la carga había
          fallado). */}
      {loadError ? (
        <div className="bg-white rounded-lg shadow p-8 md:p-12 text-center border border-red-100">
          <LayoutGrid className="w-12 md:w-16 h-12 md:h-16 mx-auto text-red-300 mb-4" />
          <h2 className="text-lg md:text-xl font-semibold text-gray-700 mb-2">
            No se pudieron cargar los datos
          </h2>
          <p className="text-gray-500 mb-6 text-sm md:text-base">
            {loadError}
          </p>
          <button
            onClick={() => {
              setLoading(true);
              fetchData();
            }}
            className="inline-flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm md:text-base"
          >
            <RefreshCw className="w-4 md:w-5 h-4 md:h-5" />
            Reintentar
          </button>
        </div>
      ) : (
      <div className="flex flex-col lg:flex-row gap-6 min-h-[600px]">
        {/* Left panel - Combinations list + Form */}
        <div className="w-full lg:w-[450px] bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
          {/* Form para añadir/editar combinación */}
          <div className="p-4 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900">
                {editingComboId ? "Editar combinación" : "Añadir combinación"}
              </h3>
              {editingComboId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  <X size={14} />
                  Cancelar edición
                </button>
              )}
            </div>

            {/* Selected tables chips. F6: botón ✕ por chip para poder
                deseleccionar una mesa que no se ve en el mapa (p.ej.
                desactivada tras formar parte de una combinación existente
                que se está editando) — sin esto quedaba "atascada" en la
                selección. */}
            <div className="min-h-[40px] mb-3 flex flex-wrap gap-2">
              {selectedTableIds.length === 0 ? (
                <span className="text-sm text-gray-400 italic">
                  Selecciona mesas en el mapa →
                </span>
              ) : (
                getSelectedTableEntries().map(({ id, name, roomName }) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-800 border border-amber-300"
                  >
                    {name}
                    {/* Desambigua mesas homónimas de salas distintas (solo
                        si hay más de una sala en la ubicación actual). */}
                    {rooms.length > 1 && (
                      <span className="text-amber-700/70 font-normal">
                        · {roomName}
                      </span>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => handleTableClick(id)}
                        className="text-amber-600 hover:text-amber-900"
                        title={`Quitar ${name} de la selección`}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>

            {/* Aviso de duplicado EN VIVO: bloquea el guardado */}
            {liveDuplicateMatch && (
              <div className="mb-3 p-2.5 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200 flex items-start gap-2">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span>
                  Ya existe una combinación con estas mesas: «
                  {liveDuplicateMatch.name}»
                </span>
              </div>
            )}

            {/* Aviso de salas mezcladas (no bloqueante) */}
            {!liveDuplicateMatch && hasMixedRooms && (
              <div className="mb-3 p-2.5 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200 flex items-start gap-2">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span>Las mesas elegidas están en salas distintas</span>
              </div>
            )}

            {/* Capacity inputs */}
            <div className="flex gap-4 mb-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Máximo Pax:
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    value={maxCapacity}
                    onChange={(e) =>
                      handleMaxCapacityChange(parseInt(e.target.value) || 1)
                    }
                    disabled={!canEdit}
                    className="w-16 px-2 py-1.5 border border-gray-300 rounded-l text-center disabled:opacity-50 disabled:cursor-not-allowed"
                    min={1}
                  />
                  <button
                    onClick={() => handleMaxCapacityChange(maxCapacity + 1)}
                    disabled={!canEdit}
                    className="px-2 py-1.5 bg-gray-100 border-t border-b border-gray-300 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                  </button>
                  <button
                    onClick={() =>
                      handleMaxCapacityChange(Math.max(1, maxCapacity - 1))
                    }
                    disabled={!canEdit}
                    className="px-2 py-1.5 bg-gray-100 border border-gray-300 rounded-r hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minus size={16} />
                  </button>
                </div>
              </div>

              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Mínimo Pax:
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    value={minCapacity}
                    onChange={(e) =>
                      handleMinCapacityChange(parseInt(e.target.value) || 1)
                    }
                    disabled={!canEdit}
                    className="w-16 px-2 py-1.5 border border-gray-300 rounded-l text-center disabled:opacity-50 disabled:cursor-not-allowed"
                    min={1}
                  />
                  <button
                    onClick={() => handleMinCapacityChange(minCapacity + 1)}
                    disabled={!canEdit}
                    className="px-2 py-1.5 bg-gray-100 border-t border-b border-gray-300 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                  </button>
                  <button
                    onClick={() =>
                      handleMinCapacityChange(Math.max(1, minCapacity - 1))
                    }
                    disabled={!canEdit}
                    className="px-2 py-1.5 bg-gray-100 border border-gray-300 rounded-r hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minus size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Aviso no bloqueante: el máximo introducido supera la suma de
                capacidades físicas de las mesas seleccionadas */}
            {!liveDuplicateMatch && exceedsPhysicalCapacity && (
              <div className="mb-2 p-2.5 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200 flex items-start gap-2">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span>
                  Supera la capacidad física de las mesas seleccionadas (
                  {suggestedCapacity.max})
                </span>
              </div>
            )}

            {/* Error de validación (mín > máx) o del backend (409 u otros) */}
            {formError && (
              <div className="mb-2 p-2.5 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200">
                {formError}
              </div>
            )}

            {/* Save/Add button */}
            <button
              onClick={handleSaveCombination}
              disabled={
                !canEdit ||
                saving ||
                selectedTableIds.length < 2 ||
                !!liveDuplicateMatch ||
                !!capacityRangeError
              }
              className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : editingComboId ? (
                <Check size={18} />
              ) : (
                <Plus size={18} />
              )}
              {editingComboId ? "GUARDAR CAMBIOS" : "AÑADIR COMBINACIÓN"}
            </button>
          </div>

          {/* Combinations list */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3 gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase">
                Combinaciones existentes
                {/* Deja claro que la lista está filtrada por la pestaña de
                    sala activa del mapa (solo si hay más de una sala). */}
                {rooms.length > 1 && selectedRoom && (
                  <span className="normal-case font-normal text-gray-400">
                    {" "}
                    · {selectedRoom.name}
                  </span>
                )}
              </span>
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideAutomatic}
                  onChange={(e) => setHideAutomatic(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Ocultar automáticas
              </label>
            </div>

            {visibleCombinations.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                {combinations.length === 0
                  ? "No hay combinaciones creadas"
                  : afterAutomaticFilter.length === 0
                    ? "No hay combinaciones que mostrar (todas son automáticas)"
                    : "No hay combinaciones en esta sala"}
              </p>
            ) : (
              <div className="space-y-3">
                {visibleCombinations.map((combo) => {
                  const isBeingEdited = editingComboId === combo.id;
                  const duplicateInfo = duplicateInfoByComboId.get(combo.id);
                  const comboRoomNames = getComboRoomNames(combo);
                  // F8: valores "en edición" del editor rápido (mientras el
                  // debounce no ha confirmado todavía contra el backend);
                  // cae a los valores del servidor si no hay nada pendiente.
                  const draftCap = getDraftCapacity(combo);
                  return (
                    <div
                      key={combo.id}
                      className={`bg-white rounded-lg border p-3 ${
                        isBeingEdited
                          ? "border-primary-400 ring-2 ring-primary-200 bg-primary-50/40"
                          : "border-gray-200"
                      }`}
                    >
                      {/* Combination name (table names) + badges */}
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className="font-medium text-gray-900 text-sm">
                          {combo.name}
                        </span>
                        {combo.isAdHoc && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-300">
                            <Bot size={11} />
                            Automática
                          </span>
                        )}
                        {duplicateInfo && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-300">
                            <AlertTriangle size={11} />
                            Duplicada de «{duplicateInfo.originalName}»
                          </span>
                        )}
                      </div>

                      {/* Sala(s) de la combinación: desambigua combinaciones
                          con mesas homónimas de salas distintas (p.ej. dos
                          combinaciones que se ven igual, "Mesas: Mesa 22,
                          Mesa 5", una en "Salón" y otra en "Feria Casa
                          Ceuta"). Solo se pinta si la ubicación tiene más de
                          una sala. */}
                      {rooms.length > 1 && comboRoomNames.length > 0 && (
                        <p className="text-xs text-gray-500 mb-1.5">
                          {comboRoomNames.length > 1
                            ? `Salas: ${comboRoomNames.join(", ")}`
                            : `Sala: ${comboRoomNames[0]}`}
                        </p>
                      )}

                      {isBeingEdited ? (
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs text-primary-700">
                            Editando en el formulario de arriba ↑
                          </span>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 text-gray-500 rounded hover:bg-gray-100 transition-colors"
                            title="Cancelar edición"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        /* Capacity controls. F8: onChange solo actualiza el
                           valor local (`draftCap`, respuesta instantánea) y
                           programa la confirmación con debounce de 500ms;
                           `onBlur` confirma al instante sin esperar. F2/F7:
                           deshabilitado mientras `saving` o sin permiso de
                           edición, con mínimo 1. */
                        <div className="flex items-center gap-4 mb-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">
                              Máx:
                            </span>
                            <input
                              type="number"
                              value={draftCap.maxCapacity}
                              onChange={(e) =>
                                scheduleCapacityChange(
                                  combo,
                                  "maxCapacity",
                                  parseInt(e.target.value) || 1,
                                )
                              }
                              onBlur={() => flushCapacityChange(combo.id)}
                              disabled={saving || !canEdit}
                              className="w-12 px-1 py-0.5 border border-gray-300 rounded text-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              min={1}
                            />
                            <button
                              onClick={() =>
                                scheduleCapacityChange(
                                  combo,
                                  "maxCapacity",
                                  draftCap.maxCapacity + 1,
                                )
                              }
                              disabled={saving || !canEdit}
                              className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus size={14} />
                            </button>
                            <button
                              onClick={() =>
                                scheduleCapacityChange(
                                  combo,
                                  "maxCapacity",
                                  Math.max(1, draftCap.maxCapacity - 1),
                                )
                              }
                              disabled={saving || !canEdit}
                              className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Minus size={14} />
                            </button>
                          </div>

                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">
                              Mín:
                            </span>
                            <input
                              type="number"
                              value={draftCap.minCapacity}
                              onChange={(e) =>
                                scheduleCapacityChange(
                                  combo,
                                  "minCapacity",
                                  parseInt(e.target.value) || 1,
                                )
                              }
                              onBlur={() => flushCapacityChange(combo.id)}
                              disabled={saving || !canEdit}
                              className="w-12 px-1 py-0.5 border border-gray-300 rounded text-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              min={1}
                            />
                            <button
                              onClick={() =>
                                scheduleCapacityChange(
                                  combo,
                                  "minCapacity",
                                  draftCap.minCapacity + 1,
                                )
                              }
                              disabled={saving || !canEdit}
                              className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus size={14} />
                            </button>
                            <button
                              onClick={() =>
                                scheduleCapacityChange(
                                  combo,
                                  "minCapacity",
                                  Math.max(1, draftCap.minCapacity - 1),
                                )
                              }
                              disabled={saving || !canEdit}
                              className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Minus size={14} />
                            </button>
                          </div>

                          {/* Actions */}
                          <div className="ml-auto flex gap-1">
                            <button
                              onClick={() => startEdit(combo)}
                              disabled={saving || !canEdit}
                              className="p-1.5 bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors disabled:opacity-50"
                              title="Editar mesas"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(combo.id)}
                              disabled={saving || !canEdit}
                              className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right panel - Map */}
        <div className="flex-1 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[500px]">
          {/* Room tabs */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 border-b border-gray-200 overflow-x-auto">
            {rooms.map((room) => (
              <button
                key={room.id}
                onClick={() => handleRoomChange(room)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedRoom?.id === room.id
                    ? "bg-primary-100 text-primary-700 border border-primary-300"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {room.name}
              </button>
            ))}
          </div>

          {/* Grid map: recuadro fijo (RECUADRO_PX) con zoom dinámico
              (auto-encuadre + lupa) dentro. Lienzo real de 50x50 celdas a
              `viewport.cellPx` px/celda, más grande que el recuadro — de ahí
              el pan. */}
          <div className="flex-1 p-4 overflow-hidden">
            {selectedRoom ? (
              <div
                className="relative w-full mx-auto"
                style={{ maxWidth: RECUADRO_PX }}
              >
                <div
                  ref={viewport.containerRef}
                  onMouseDown={viewport.onMouseDown}
                  onTouchStart={viewport.onTouchStart}
                  onScroll={viewport.onScroll}
                  className={`w-full overflow-auto border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 ${
                    viewport.isPanning ? "cursor-grabbing" : ""
                  }`}
                  style={{ height: "min(540px, 65vh)" }}
                >
                  <div
                    className="relative"
                    style={{
                      width: CANVAS_CELLS * viewport.cellPx,
                      height: CANVAS_CELLS * viewport.cellPx,
                    }}
                  >
                    {/* Grid lines */}
                    <div className="absolute inset-0 pointer-events-none">
                      {Array.from({ length: CANVAS_CELLS }).map((_, i) => (
                        <div
                          key={`v-${i}`}
                          className="absolute top-0 bottom-0 border-l border-gray-200"
                          style={{ left: `${(i / CANVAS_CELLS) * 100}%` }}
                        />
                      ))}
                      {Array.from({ length: CANVAS_CELLS }).map((_, i) => (
                        <div
                          key={`h-${i}`}
                          className="absolute left-0 right-0 border-t border-gray-200"
                          style={{ top: `${(i / CANVAS_CELLS) * 100}%` }}
                        />
                      ))}
                    </div>

                    {/* Tables */}
                    {selectedRoom.tables
                      .filter((t) => t.isActive)
                      .map((table) => {
                        const isSelected = selectedTableIds.includes(
                          table.id,
                        );
                        const tableCombos = getTableCombinations(table.id);
                        const hasCombination = tableCombos.length > 0;

                        // Determine shape classes
                        const shapeClass =
                          table.shape === "circle"
                            ? "rounded-full"
                            : "rounded-sm";

                        // Colors based on state (matching TableMap style) —
                        // sin tocar: solo se afina el grosor del borde.
                        let bgColor = "bg-primary-100 hover:bg-primary-200";
                        let borderColor = "border-primary-500";
                        let textColor = "text-primary-800";

                        if (isSelected) {
                          bgColor = "bg-green-100";
                          borderColor =
                            "border-green-500 ring-2 ring-green-300";
                          textColor = "text-green-800";
                        } else if (hasCombination) {
                          bgColor = "bg-orange-100 hover:bg-orange-200";
                          borderColor = "border-orange-500";
                          textColor = "text-orange-800";
                        }

                        const cellPct = 100 / CANVAS_CELLS;
                        const boxWidthPx =
                          (table.width || 1) * viewport.cellPx;
                        const boxHeightPx =
                          (table.height || 1) * viewport.cellPx;
                        const { fontPx: nameFontPx, lines: nameLines } =
                          fitNameToTwoLines(
                            table.name,
                            boxWidthPx,
                            clampNum(viewport.cellPx * 0.5, 8, 14),
                            8,
                          );
                        const capFontPx = clampNum(
                          viewport.cellPx * 0.4,
                          7,
                          11,
                        );
                        const lineCount = countFittingLines(boxHeightPx, [
                          ...Array(nameLines).fill(nameFontPx),
                          capFontPx,
                        ]);
                        const capThreshold = nameLines + 1;

                        return (
                          <div
                            key={table.id}
                            onClick={() => handleTableClick(table.id)}
                            className={`absolute ${canEdit ? "cursor-pointer" : "cursor-not-allowed"} flex items-center justify-center overflow-hidden transition-all
                          ${shapeClass} ${bgColor} ${borderColor} ${textColor} border hover:shadow-lg
                        `}
                            style={{
                              left: `calc(${table.positionX * cellPct}% + 1px)`,
                              top: `calc(${table.positionY * cellPct}% + 1px)`,
                              width: `calc(${(table.width || 1) * cellPct}% - 2px)`,
                              height: `calc(${(table.height || 1) * cellPct}% - 2px)`,
                            }}
                            title={`${table.name} (${table.minCapacity}-${table.maxCapacity} personas)${
                              hasCombination
                                ? `\nCombinaciones: ${tableCombos.map((c) => c.name).join(", ")}`
                                : ""
                            }`}
                          >
                            <div className="text-center leading-none px-0.5 max-w-full">
                              {lineCount >= 1 && (
                                <div
                                  className="font-normal break-words line-clamp-2"
                                  style={{ fontSize: nameFontPx }}
                                >
                                  {table.name}
                                </div>
                              )}
                              {lineCount >= capThreshold && (
                                <div
                                  className="opacity-75 truncate"
                                  style={{ fontSize: capFontPx }}
                                >
                                  {table.minCapacity}-{table.maxCapacity}
                                </div>
                              )}
                            </div>
                            {isSelected && (
                              <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5">
                                <Check size={12} className="text-white" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
                <ZoomControls
                  onZoomIn={viewport.zoomIn}
                  onZoomOut={viewport.zoomOut}
                  onFit={() =>
                    viewport.fitToContent(
                      computeContentBBox(selectedRoom.tables ?? []),
                    )
                  }
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No hay salas disponibles
              </div>
            )}
          </div>

          {/* Legend. F9: flex-wrap para que no se desborde en móvil. */}
          <div className="p-3 bg-white border-t border-gray-200 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-primary-100 rounded border-2 border-primary-500" />
              <span className="text-gray-600">Mesa disponible</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 rounded border-2 border-green-500" />
              <span className="text-gray-600">Mesa seleccionada</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-100 rounded border-2 border-orange-500" />
              <span className="text-gray-600">Con combinación existente</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {confirmDialog.title}
              </h3>
              <p className="text-gray-600">{confirmDialog.message}</p>
            </div>
            <div className="flex gap-3 p-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() =>
                  setConfirmDialog({
                    show: false,
                    title: "",
                    message: "",
                    onConfirm: () => {},
                  })
                }
                className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
